// `./contracts` — query-key registry for the workspace domain (arch §5.1).
// Key strings are FROZEN verbatim from their pre-move definitions: renaming a
// key orphans every user's cache (and `GET_USER` additionally keys backend-
// synced behaviour). Importable by any DAG-compatible consumer, incl. foreign
// `data/` layers (Aperture 1).

/** GET /user — moved verbatim from `src/hooks/query/current-user-cache.ts`. */
export const GET_USER = 'getUser';

/** CIBA device status — moved verbatim from the Login with Heidi App hook. */
export const CIBA_DEVICE_STATUS_QUERY_KEY = 'cibaDeviceStatus';

/** GET /access-entitlements — moved verbatim from `useQueryEntitlements.ts`. */
export const GET_ENTITLEMENTS = 'getEntitlements';

/**
 * Team-admin entitlement-administration cache identities (EN4, ARCFE-1025).
 *
 * Both strings are FROZEN verbatim from their legacy declaration sites
 * (`src/hooks/query/useQueryTeamAccessEntitlements.ts` and
 * `…TeamMemberAccessEntitlements.ts`), which are now re-exports. They moved
 * because the six entitlement WRITE hooks invalidate against them and those
 * hooks are headless now — a package `/data` module cannot reach `@/hooks/…`
 * for a key. Re-exported rather than re-declared on purpose: two independent
 * declarations of one cache key split writes and reads across two caches the
 * first time either literal is edited (the ARCFE-1001 finding).
 */
export const GET_TEAM_ACCESS_ENTITLEMENTS = 'getTeamAccessEntitlements';
export const GET_TEAM_MEMBER_ACCESS_ENTITLEMENTS = 'getTeamMemberAccessEntitlements';

/** GET /organization/members/search — moved verbatim from `useQueryOrganizationMembersSearch.ts`. */
export const SEARCH_ORGANIZATION_MEMBERS = 'organizationMembersSearch';

/** GET /organization/{id}/teams — moved verbatim from `useQueryOrganizationTeams.ts`. */
export const QUERY_ORGANIZATION_TEAMS = 'organizationTeams';

/**
 * Key factories mirroring the exact key shapes the hooks build today.
 * The entitlements key deliberately includes the auth token — a Heidi token
 * refresh re-keys the query so the SCC coding JWT is reminted (RCM-851).
 */
/** Org-by-id cache — `useQueryOrganizationById` (OT3, ARCFE-954). */
export const GET_ORGANIZATION_BY_ORGANIZATION_ID = 'getOrganizationByOrganizationId';

/** Org members list — `useQueryOrganizationMembers` (OT3). */
export const GET_ORGANIZATION_MEMBERS = 'getOrganizationMembers';

/** Logout-everywhere job poll — `useQueryOrganizationLogoutJob` (OT3). */
export const GET_ORGANIZATION_LOGOUT_JOB = 'getOrganizationLogoutJob';

/** Joined organizations — `useQueryJoinedOrganizations` (OT3 slice 2, ARCFE-955). */
export const GET_JOINED_ORGANIZATION = 'getJoinedOrganization';

/** Joined teams — `useQueryJoinedTeams` (OT3 slice 2). */
export const GET_JOINED_TEAMS = 'getJoinedTeams';

/** Team details — `useQueryTeamDetails` (OT3 slice 2). */
export const GET_TEAM_DETAILS = 'getTeamDetails';

/** Roles catalog — `useQueryRoles` (OT3 slice 2). */
export const GET_ROLES = 'getRoles';

/**
 * Team members + relationship — `useQueryTeamMembersWithRelationship` (OT4,
 * ARCFE-1033).
 *
 * FROZEN verbatim from `src/hooks/query/useQueryTeamMembersWithRelationship.ts`,
 * which is now a re-export. It moved for the same reason the entitlement pair
 * did at EN4: two headless write hooks (`useMutateUpdateUserRole` and
 * `useMutateToggleTeamSettings`) invalidate against it, and a package `/data`
 * module cannot reach `@/hooks/…` for a key. Re-exported rather than
 * re-declared — two independent declarations of one cache key split writes and
 * reads across two caches the first time either literal is edited (ARCFE-1001).
 */
export const GET_TEAM_MEMBERS_WITH_RELATIONSHIP = 'getTeamMembersWithRelationship';

/**
 * Team shortcuts list — `useQueryTeamShortcuts` (D2, ARCFE-1062).
 *
 * FROZEN verbatim from `src/hooks/query/useQueryTeamShortcuts.ts`, which is now
 * a re-export of both the constant and the factory below. Moved for the same
 * reason `GET_TEAM_MEMBERS_WITH_RELATIONSHIP` did at OT4: the three headless
 * `useMutateTeamShortcut` hooks read, write and invalidate against this
 * identity, and a package `/data` module cannot reach `@/hooks/…` for a key.
 * Re-exported rather than re-declared — two independent declarations of one
 * cache key split writes and reads across two caches the first time either
 * literal is edited (ARCFE-1001).
 */
export const GET_TEAM_SHORTCUTS = 'getTeamShortcuts';

/**
 * The exact key tuple the shortcut hooks build. Moved with the constant because
 * the mutations' optimistic `setQueryData`/`cancelQueries` calls and the query's
 * `queryKey` must stay the SAME tuple — re-deriving `[GET_TEAM_SHORTCUTS, teamId]`
 * at each site is how a read and a write drift onto two caches.
 */
export const getTeamShortcutsQueryKey = (teamId: string | undefined) =>
  [GET_TEAM_SHORTCUTS, teamId] as const;

/** Capability definitions — `useQueryCapabilities` (IP2, ARCFE-956). */
export const GET_CAPABILITIES = 'getCapabilities';

/** User capability configs — `useQueryUserCapabilities` (IP2). */
export const GET_USER_CAPABILITIES = 'getUserCapabilities';

/** Capability autocomplete — `useQueryCapabilityAutocomplete` (IP2). */
export const GET_CAPABILITY_AUTOCOMPLETE = 'getCapabilityAutocomplete';

/** User snippets — `useQueryUserSnippets` (IP2). */
export const GET_USER_SNIPPETS = 'getUserSnippets';

/** User personalizations — `useQueryUserPersonalizations` (IP2; the British-S
 * const name is frozen legacy spelling, the string is the cache identity). */
export const GET_USER_PERSONALISATIONS = 'getUserPersonalizations';

export const workspaceKeys = {
  user: () => [GET_USER] as const,
  entitlements: (userRefId: string | undefined, token: string | null | undefined) =>
    [GET_ENTITLEMENTS, userRefId, token] as const,
  organizationMembersSearch: (q: string) => [SEARCH_ORGANIZATION_MEMBERS, q] as const,
  organizationTeams: (orgId: string | null) => [QUERY_ORGANIZATION_TEAMS, orgId] as const,
};

/**
 * Active-workspace family cache identities (AW1, ARCFE-984). GET_WORKSPACES'
 * legacy home was a deliberately dependency-free module so
 * `scribeHttpErrorHandler` could target the key without a hook import —
 * a constraint the contracts door satisfies by construction.
 */
export const GET_WORKSPACES = 'getWorkspaces';
export const GET_WORKSPACE_INVITATIONS = 'getWorkspaceInvitations';

/** Resolved `workspace-share_patients_enabled` (shared patient directory). */
export const GET_SHARED_PATIENT_DIRECTORY_SETTING = 'getSharedPatientDirectorySetting';

/** Team-shaped invitations listing — a different endpoint from the switcher's. */
export const GET_PENDING_TEAM_INVITATIONS = 'getPendingTeamInvitations';

/** Preview count for "log out all matching". */
export const GET_LOGOUT_ELIGIBLE_COUNT = 'getLogoutEligibleCount';

/** One-time org analytics dashboard token. */
export const GET_ORG_ANALYTICS_DASHBOARD = 'get_org_analytics_dashboard';

/** One-time team analytics dashboard token. */
export const GET_TEAM_ANALYTICS_DASHBOARD = 'get_team_analytics_dashboard';

/** Team details read used by join and onboarding, distinct from `GET_TEAM_DETAILS`. */
export const GET_TEAM_BASIC_DETAILS = 'getTeamBasicDetails';

/** Teams a self-onboarding user can join. */
export const GET_CANDIDATE_TEAMS = 'getJoinableTeams';

/** Specialty catalog. The backtick literal is the frozen cache identity. */
export const GET_SPECIALTIES = `Get_specialties`;

/** PATCH /user/auto-extract-intent. */
export const UPDATE_AUTO_EXTRACT_INTENT_MUTATION_KEY = 'updateAutoExtractIntent';

/** Email-change flow mutation identities. Literals are frozen cache keys. */
export const CHANGE_EMAIL_REQUEST = 'ChangeEmailRequest';
export const CHECK_CURRENT_EMAIL = 'CheckCurrentEmail';
export const CONFIRM_EMAIL_CHANGE = 'ConfirmEmailChange';
export const VERIFY_CURRENT_EMAIL_REQUEST = 'VerifyCurrentEmailRequest';
